import BASE_URL from '../config/BASE_URL';

/**
 * Visitor tracking.
 *
 * Records page views and interactions so the console can answer "where did this
 * lead come from". Deliberately small: no third-party script, no cookies beyond
 * two anonymous ids in browser storage, and every failure is silent — analytics
 * must never break the site.
 */

const VISITOR_KEY = 'sp_visitor_id';
const SESSION_KEY = 'sp_session_id';
const SESSION_TS_KEY = 'sp_session_ts';
const ATTRIBUTION_KEY = 'sp_attribution';
const SESSION_TIMEOUT = 30 * 60 * 1000;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

/** Browser storage throws in private mode and inside some in-app browsers. */
const safeGet = (store, key) => { try { return store.getItem(key); } catch { return null; } };
const safeSet = (store, key, value) => { try { store.setItem(key, value); } catch { /* ignore */ } };

const randomId = () => {
  try {
    return crypto.randomUUID().replace(/-/g, '');
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  }
};

// -----------------------------------------------------------------------------
// Identity
// -----------------------------------------------------------------------------
export function getVisitorId() {
  let id = safeGet(localStorage, VISITOR_KEY);
  if (!id) {
    id = randomId();
    safeSet(localStorage, VISITOR_KEY, id);
  }
  return id;
}

export function isNewVisitor() {
  return !safeGet(localStorage, `${VISITOR_KEY}_seen`);
}

export function getSessionId() {
  const now = Date.now();
  const last = Number(safeGet(sessionStorage, SESSION_TS_KEY) || 0);
  let id = safeGet(sessionStorage, SESSION_KEY);

  // A gap longer than 30 minutes starts a new session.
  if (!id || now - last > SESSION_TIMEOUT) {
    id = randomId();
    safeSet(sessionStorage, SESSION_KEY, id);
  }
  safeSet(sessionStorage, SESSION_TS_KEY, String(now));
  return id;
}

// -----------------------------------------------------------------------------
// Attribution
// -----------------------------------------------------------------------------
/**
 * Captured once, on the first page of a session, and reused for the rest of it.
 * Without this a form submitted on page four looks like a self-referral, and
 * every lead would be attributed to "direct".
 */
export function captureAttribution() {
  const existing = safeGet(sessionStorage, ATTRIBUTION_KEY);
  if (existing) { try { return JSON.parse(existing); } catch { /* re-capture */ } }

  const params = new URLSearchParams(window.location.search);
  const data = { landing_page: window.location.href.slice(0, 1000) };

  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) data[key] = value.slice(0, 180);
  });

  // gclid/fbclid mean paid traffic even when the utm tags were stripped.
  if (!data.utm_source && params.get('gclid')) {
    data.utm_source = 'google';
    data.utm_medium = 'cpc';
  }
  if (!data.utm_source && params.get('fbclid')) {
    data.utm_source = 'facebook';
    data.utm_medium = 'social';
  }

  const ref = document.referrer || '';
  if (ref && !ref.includes(window.location.host)) data.referrer = ref.slice(0, 1000);

  safeSet(sessionStorage, ATTRIBUTION_KEY, JSON.stringify(data));
  return data;
}

/** The block attached to every public POST by the axios interceptor. */
export function getAttribution() {
  return {
    ...captureAttribution(),
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
  };
}

// -----------------------------------------------------------------------------
// Page views
// -----------------------------------------------------------------------------
let currentVisit = { id: null, startedAt: 0 };

export async function trackPageView(page, { title } = {}) {
  // One view per page per session, so a re-render or a back-navigation does not
  // inflate the numbers.
  const guard = `sp_pv_${page}`;
  if (safeGet(sessionStorage, guard)) return null;
  safeSet(sessionStorage, guard, '1');

  const isFirst = !safeGet(sessionStorage, 'sp_session_entry');
  safeSet(sessionStorage, 'sp_session_entry', '1');

  const payload = {
    ...getAttribution(),
    page,
    path: window.location.pathname,
    title: title || document.title,
    screen_width: window.innerWidth,
    is_new_visitor: isNewVisitor(),
    is_entry: isFirst,
  };

  safeSet(localStorage, `${VISITOR_KEY}_seen`, '1');

  try {
    const res = await fetch(`${BASE_URL}/api/visits`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      credentials: 'include',
      keepalive: true,
    });
    const json = await res.json();
    currentVisit = { id: json?.data?.id ?? null, startedAt: Date.now() };
    return currentVisit.id;
  } catch {
    return null; // analytics never surfaces an error to the visitor
  }
}

/** Fired on unload so we know how long the page was actually read. */
function flushDuration() {
  if (!currentVisit.id) return;
  const body = JSON.stringify({
    visit_id: currentVisit.id,
    duration_ms: Date.now() - currentVisit.startedAt,
  });

  // sendBeacon survives the page going away; fetch usually does not.
  if (navigator.sendBeacon) {
    navigator.sendBeacon(`${BASE_URL}/api/visits/duration`, new Blob([body], { type: 'application/json' }));
  }
  currentVisit = { id: null, startedAt: 0 };
}

// -----------------------------------------------------------------------------
// Interaction events
// -----------------------------------------------------------------------------
let queue = [];
let flushTimer = null;

/**
 * trackEvent('cta_click', { label: 'apply-now', page: 'training-internship' })
 *
 * Events are batched for two seconds to avoid a request per click.
 */
export function trackEvent(eventName, { label, page, value, category, meta } = {}) {
  queue.push({
    ...getAttribution(),
    event_name: eventName,
    event_category: category || 'interaction',
    page: page || document.body.dataset.page || window.location.pathname.replace(/^\//, '') || 'home',
    label,
    value,
    meta: meta || {},
  });

  clearTimeout(flushTimer);
  flushTimer = setTimeout(flushEvents, 2000);
  if (queue.length >= 10) flushEvents();
}

export function flushEvents() {
  if (!queue.length) return;
  const events = queue.splice(0, queue.length);
  const body = JSON.stringify({ events });

  if (document.visibilityState === 'hidden' && navigator.sendBeacon) {
    navigator.sendBeacon(`${BASE_URL}/api/events/batch`, new Blob([body], { type: 'application/json' }));
    return;
  }

  fetch(`${BASE_URL}/api/events/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    credentials: 'include',
    keepalive: true,
  }).catch(() => { /* silent */ });
}

// -----------------------------------------------------------------------------
// Lifecycle
// -----------------------------------------------------------------------------
let initialised = false;

export function initTracker() {
  if (initialised || typeof window === 'undefined') return;
  initialised = true;

  captureAttribution();

  // 'pagehide' fires reliably on mobile Safari where 'unload' does not.
  window.addEventListener('pagehide', () => { flushDuration(); flushEvents(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') { flushDuration(); flushEvents(); }
  });
}

const tracker = {
  initTracker, trackPageView, trackEvent, flushEvents,
  getAttribution, getVisitorId, getSessionId,
};

export default tracker;
